import { Modal, Input, Form, message } from 'antd';
import { useQuery } from '@tanstack/react-query';

interface Props {
  oid: string;
  displayName: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Injected API call — each SPA passes its own `/v1/admin/*` or
   * `/v1/superadmin/*` deactivate implementation.
   */
  onDeactivate: (oid: string, reason: string) => Promise<void>;
  /**
   * Optional source for the "N captures stay attributed" count shown in the
   * body. When omitted the count renders as 0 (the modal still works).
   */
  fetchCaptureCount?: (oid: string) => Promise<number>;
}

export function DeactivateUserModal({
  oid,
  displayName,
  open,
  onClose,
  onSuccess,
  onDeactivate,
  fetchCaptureCount,
}: Props) {
  const [form] = Form.useForm<{ reason?: string }>();

  const { data: captureCount = 0, isLoading: countLoading } = useQuery({
    queryKey: ['userCaptureCount', oid],
    queryFn: () =>
      fetchCaptureCount ? fetchCaptureCount(oid) : Promise.resolve(0),
    enabled: open && !!fetchCaptureCount,
  });

  return (
    <Modal
      open={open}
      title={`Deactivate ${displayName}?`}
      okText="Deactivate"
      okButtonProps={{ danger: true }}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={async () => {
        const { reason } = await form.validateFields();
        try {
          await onDeactivate(oid, reason ?? '');
          message.success(`${displayName} has been deactivated.`);
          form.resetFields();
          onSuccess();
          onClose();
        } catch (err: unknown) {
          const msg =
            err instanceof Error
              ? err.message
              : (err as { message?: string })?.message ?? 'Deactivation failed';
          void message.error(msg);
        }
      }}
    >
      <p>
        <strong>{displayName}</strong> will lose access immediately. Their{' '}
        <strong>{countLoading ? '…' : captureCount}</strong> captures stay
        attributed to them and remain visible to managers. You can reactivate
        them any time.
      </p>
      <Form form={form} layout="vertical">
        <Form.Item name="reason" label="Reason (optional)">
          <Input.TextArea
            rows={3}
            placeholder="e.g. Left the company 2026-06-15"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
